import type { LinkPreview } from '@flock-types/chat.types';

const URL_PATTERN = /(?:(?:https?:\/\/)|(?:www\.))?[a-z0-9][a-z0-9-]*(?:\.[a-z][a-z0-9-]{1,})+(?::\d+)?(?:\/[^\s<>"']*)?/i;
const URL_PATTERN_GLOBAL = /(?:(?:https?:\/\/)|(?:www\.))?[a-z0-9][a-z0-9-]*(?:\.[a-z][a-z0-9-]{1,})+(?::\d+)?(?:\/[^\s<>"']*)?/gi;
const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;
const TIKTOK_ID_PATTERN = /^\d{5,30}$/;
const FACEBOOK_HOSTS = new Set([
  'facebook.com',
  'm.facebook.com',
  'mobile.facebook.com',
  'web.facebook.com',
  'fb.watch',
  'fb.me',
]);
const GENERIC_FALLBACK_FRESH_MS = 5 * 60 * 1000;

export const FALLBACK_EXCLUDED_SCHEMES = ['mailto:', 'tel:', 'flock:'];

export const FALLBACK_EXCLUDED_FILE_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.zip',
  '.rar',
  '.7z',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.mp4',
  '.mov',
  '.mp3',
  '.m4a',
];

export const FALLBACK_EXCLUDED_HOST_PATTERNS = [
  /(^|\.)zoom\.us$/i,
  /(^|\.)meet\.google\.com$/i,
  /(^|\.)teams\.microsoft\.com$/i,
  /(^|\.)webex\.com$/i,
  /(^|\.)slack\.com$/i,
  /(^|\.)maps\.google\.com$/i,
  /^maps\.app\.goo\.gl$/i,
  /^goo\.gl$/i,
  /(^|\.)maps\.apple\.com$/i,
  /^docs\.google\.com$/i,
  /^drive\.google\.com$/i,
  /^apps\.apple\.com$/i,
  /^play\.google\.com$/i,
  /(^|\.)firebaseapp\.com$/i,
  /(^|\.)web\.app$/i,
  /^localhost$/i,
  /^127\./,
  /^10\./,
  /^172\.(1[6-9]|2[0-9]|3[01])\./,
  /^192\.168\./,
];

/**
 * True when a pattern match is actually part of an email address:
 * either the local part ("test.person|@gmail.com") or the domain part
 * ("test.person@|gmail.com"). The URL pattern allows bare domains, so
 * without this guard emails get treated as URLs — stripped from the
 * message text and rendered as broken link previews.
 */
function isEmailAddressContext(text: string, start: number, end: number): boolean {
  if (text[end] === '@') return true;
  if (start > 0 && text[start - 1] === '@') return true;
  return false;
}

export function extractFirstUrl(text?: string): string | null {
  if (!text) return null;

  for (const match of text.matchAll(URL_PATTERN_GLOBAL)) {
    const candidate = trimTrailingPunctuation(match[0]);
    if (!candidate) continue;
    const start = match.index ?? 0;
    if (isEmailAddressContext(text, start, start + candidate.length)) continue;
    return normalizeUrl(candidate);
  }

  return null;
}

function trimTrailingPunctuation(url: string): string {
  return url.replace(/[),.!?;:]+$/g, '');
}

function normalizeUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function parseUrl(url: string): URL | null {
  try {
    return new URL(normalizeUrl(url));
  } catch {
    return null;
  }
}

function normalizedHost(url: string): string {
  const parsed = parseUrl(url);
  if (!parsed) return '';
  return parsed.hostname.toLowerCase().replace(/^www\./, '');
}

export function parseYouTubeVideoId(url: string): string | null {
  const parsed = parseUrl(url);
  if (!parsed) return null;

  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  let id: string | null = null;

  if (host === 'youtu.be') {
    id = parsed.pathname.split('/').filter(Boolean)[0] || null;
  } else if (host === 'youtube.com' || host === 'm.youtube.com') {
    if (parsed.pathname === '/watch') {
      id = parsed.searchParams.get('v');
    } else {
      const parts = parsed.pathname.split('/').filter(Boolean);
      if (['embed', 'shorts', 'live'].includes(parts[0])) {
        id = parts[1] || null;
      }
    }
  }

  return id && YOUTUBE_ID_PATTERN.test(id) ? id : null;
}

export function parseXPostId(url: string): string | null {
  const parsed = parseUrl(url);
  if (!parsed) return null;

  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  if (!['x.com', 'twitter.com', 'mobile.twitter.com'].includes(host)) return null;

  const parts = parsed.pathname.split('/').filter(Boolean);
  const statusIndex = parts.findIndex((part) => part === 'status' || part === 'statuses');
  const id = statusIndex >= 0 ? parts[statusIndex + 1] : null;

  return id && /^\d+$/.test(id) ? id : null;
}

export function parseTikTokVideoId(url: string): string | null {
  const parsed = parseUrl(url);
  if (!parsed) return null;

  const host = parsed.hostname.toLowerCase();
  if (!['tiktok.com', 'www.tiktok.com', 'm.tiktok.com'].includes(host)) return null;

  const parts = parsed.pathname.split('/').filter(Boolean);
  let id: string | null = null;

  const videoIndex = parts.findIndex((part) => part === 'video');
  if (videoIndex >= 0) {
    id = parts[videoIndex + 1] || null;
  } else if (parts[0] === 'v') {
    id = (parts[1] || '').replace(/\.html$/i, '');
  } else if (parts[0] === 'player' && parts[1] === 'v1') {
    id = parts[2] || null;
  }

  return id && TIKTOK_ID_PATTERN.test(id) ? id : null;
}

export function buildOptimisticLinkPreview(url: string): LinkPreview {
  const normalized = normalizeUrl(url);
  const host = normalizedHost(normalized);
  const youtubeId = parseYouTubeVideoId(normalized);

  if (youtubeId) {
    return {
      url: normalized,
      provider: 'youtube',
      embedType: 'youtube-video',
      providerId: youtubeId,
      canonicalUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
      embedUrl: `https://www.youtube-nocookie.com/embed/${youtubeId}?playsinline=1&rel=0&modestbranding=1`,
      thumbnailUrl: `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
      image: `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
      siteName: 'YouTube',
    };
  }

  const xPostId = parseXPostId(normalized);
  if (xPostId) {
    return {
      url: normalized,
      provider: 'x',
      embedType: 'x-post',
      providerId: xPostId,
      canonicalUrl: canonicalizeXUrl(normalized),
      siteName: 'X',
    };
  }

  if (host === 'tiktok.com' || host === 'm.tiktok.com' || host === 'vm.tiktok.com') {
    const tiktokId = parseTikTokVideoId(normalized);

    if (tiktokId) {
      return {
        url: normalized,
        provider: 'tiktok',
        embedType: 'tiktok-video',
        providerId: tiktokId,
        canonicalUrl: canonicalizeTikTokUrl(normalized),
        siteName: 'TikTok',
      };
    }

    return {
      url: normalized,
      provider: 'tiktok',
      embedType: 'none',
      canonicalUrl: normalized,
      siteName: 'TikTok',
    };
  }

  if (FACEBOOK_HOSTS.has(host)) {
    return {
      url: normalized,
      title: 'Facebook',
      provider: 'facebook',
      embedType: 'none',
      canonicalUrl: normalized,
      siteName: 'Facebook',
    };
  }

  return {
    url: normalized,
    provider: 'generic',
    embedType: 'none',
    siteName: host || undefined,
  };
}

function canonicalizeXUrl(url: string): string {
  const parsed = parseUrl(url);
  const postId = parseXPostId(url);
  if (!parsed || !postId) return normalizeUrl(url);

  const parts = parsed.pathname.split('/').filter(Boolean);
  const handle = parts[0] || 'i';
  return `https://x.com/${handle}/status/${postId}`;
}

export function canonicalizeTikTokUrl(url: string): string {
  const parsed = parseUrl(url);
  const videoId = parseTikTokVideoId(url);
  if (!parsed || !videoId) return normalizeUrl(url);

  const parts = parsed.pathname.split('/').filter(Boolean);
  const videoIndex = parts.findIndex((part) => part === 'video');
  const handle = videoIndex > 0 && parts[videoIndex - 1]?.startsWith('@') ? parts[videoIndex - 1] : null;

  if (handle) {
    return `https://www.tiktok.com/${handle}/video/${videoId}`;
  }

  return `https://www.tiktok.com/player/v1/${videoId}`;
}

export function mergeLinkPreview(base: LinkPreview, enriched?: LinkPreview | null): LinkPreview {
  if (!enriched) return base;
  const keepBaseProvider =
    base.provider !== undefined &&
    base.provider !== 'generic' &&
    (!enriched.provider || enriched.provider === 'generic');

  return removeUndefinedLinkPreviewFields({
    ...base,
    ...enriched,
    provider: keepBaseProvider ? base.provider : enriched.provider || base.provider,
    embedType: keepBaseProvider ? base.embedType : enriched.embedType || base.embedType,
    providerId: enriched.providerId || base.providerId,
    canonicalUrl: enriched.canonicalUrl || base.canonicalUrl,
    embedUrl: enriched.embedUrl || base.embedUrl,
    thumbnailUrl: enriched.thumbnailUrl || base.thumbnailUrl,
    image: enriched.image || enriched.thumbnailUrl || base.image || base.thumbnailUrl,
    authorName: enriched.authorName || base.authorName,
    authorUrl: enriched.authorUrl || base.authorUrl,
    htmlWidth: enriched.htmlWidth || base.htmlWidth,
    htmlHeight: enriched.htmlHeight || base.htmlHeight,
  });
}

export function removeUndefinedLinkPreviewFields(preview: LinkPreview): LinkPreview {
  return Object.fromEntries(
    Object.entries(preview).filter(([, value]) => value !== undefined)
  ) as LinkPreview;
}

function timestampToMillis(timestamp: unknown): number | null {
  if (!timestamp) return null;
  if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp === 'object' && timestamp !== null && 'toMillis' in timestamp) {
    const millis = (timestamp as { toMillis: () => number }).toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  return null;
}

export function isGenericFallbackExcluded(url: string): boolean {
  const rawScheme = url.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase();
  if (rawScheme && rawScheme !== 'http' && rawScheme !== 'https') {
    return true;
  }

  const parsed = parseUrl(url);
  if (!parsed) return true;

  const scheme = parsed.protocol.toLowerCase();
  if (FALLBACK_EXCLUDED_SCHEMES.includes(scheme)) return true;

  const host = parsed.hostname.toLowerCase();
  if (FALLBACK_EXCLUDED_HOST_PATTERNS.some((pattern) => pattern.test(host))) return true;

  const pathname = parsed.pathname.toLowerCase();
  return FALLBACK_EXCLUDED_FILE_EXTENSIONS.some((extension) => pathname.endsWith(extension));
}

export function shouldShowGenericFallbackPreview(
  url: string,
  messageCreatedAt?: unknown,
  now = Date.now()
): boolean {
  if (isGenericFallbackExcluded(url)) return false;

  const createdAtMillis = timestampToMillis(messageCreatedAt);
  if (!createdAtMillis) return true;

  return now - createdAtMillis <= GENERIC_FALLBACK_FRESH_MS;
}

export function stripPreviewUrlFromText(text?: string, preview?: LinkPreview): string {
  if (!text || !preview) return text || '';

  for (const match of text.matchAll(URL_PATTERN_GLOBAL)) {
    const rawUrl = match[0];
    const candidateUrl = trimTrailingPunctuation(rawUrl);
    const start = match.index ?? 0;
    const end = start + candidateUrl.length;

    if (isEmailAddressContext(text, start, end)) continue;

    if (candidateUrl && previewMatchesUrl(candidateUrl, preview)) {
      return cleanTextAfterUrlRemoval(`${text.slice(0, start)}${text.slice(end)}`);
    }
  }

  return text;
}

function previewMatchesUrl(url: string, preview: LinkPreview): boolean {
  const candidate = normalizeUrl(url);
  const previewUrls = [preview.url, preview.canonicalUrl].filter((value): value is string => Boolean(value));

  if (previewUrls.some((previewUrl) => comparableUrl(previewUrl) === comparableUrl(candidate))) {
    return true;
  }

  const optimistic = buildOptimisticLinkPreview(candidate);
  if (optimistic.provider && preview.provider && optimistic.provider !== preview.provider) {
    return false;
  }

  if (optimistic.providerId && preview.providerId && optimistic.providerId === preview.providerId) {
    return true;
  }

  return previewUrls.some((previewUrl) => looseComparableUrl(previewUrl) === looseComparableUrl(candidate));
}

function comparableUrl(url: string): string {
  const parsed = parseUrl(url);
  if (!parsed) return normalizeUrl(url).toLowerCase();

  parsed.hash = '';
  parsed.protocol = parsed.protocol.toLowerCase();
  parsed.hostname = parsed.hostname.toLowerCase();
  if (parsed.pathname !== '/') {
    parsed.pathname = parsed.pathname.replace(/\/$/, '');
  }

  return parsed.href;
}

function looseComparableUrl(url: string): string {
  const parsed = parseUrl(url);
  if (!parsed) return normalizeUrl(url).toLowerCase().replace(/^https?:\/\/(www\.)?/, '');

  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  const pathname = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
  return `${host}${pathname}${parsed.search}`;
}

function cleanTextAfterUrlRemoval(text: string): string {
  return text
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .trim();
}

export function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function buildYouTubeEmbedHtml(preview: LinkPreview): string {
  const embedUrl = preview.embedUrl;
  if (!embedUrl) return '';
  const safeUrl = escapeHtmlAttribute(embedUrl);

  return `<!doctype html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <style>
    html, body { margin: 0; padding: 0; background: #000; overflow: hidden; }
    .wrap { position: relative; width: 100%; padding-top: 56.25%; }
    iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
  </style>
</head>
<body>
  <div class="wrap">
    <iframe
      src="${safeUrl}"
      title="YouTube video player"
      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowfullscreen>
    </iframe>
  </div>
</body>
</html>`;
}

export function buildXPostEmbedHtml(preview: LinkPreview): string {
  const postUrl = preview.canonicalUrl || preview.url;
  const safeUrl = escapeHtmlAttribute(postUrl);

  return `<!doctype html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <style>
    html, body { margin: 0; padding: 0; background: transparent; overflow: hidden; }
    body { display: flex; justify-content: center; }
    .container { width: 100%; max-width: 550px; }
  </style>
</head>
<body>
  <div class="container">
    <blockquote class="twitter-tweet" data-dnt="true" data-theme="light">
      <a href="${safeUrl}"></a>
    </blockquote>
  </div>
  <script async src="https://platform.x.com/widgets.js" charset="utf-8"></script>
  <script>
    function postHeight() {
      var height = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      window.ReactNativeWebView && window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'height', height: height }));
    }
    window.addEventListener('load', function() {
      setTimeout(postHeight, 500);
      setTimeout(postHeight, 1500);
      setTimeout(postHeight, 3000);
    });
    var observer = new MutationObserver(postHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
  </script>
</body>
</html>`;
}

export function buildTikTokEmbedHtml(preview: LinkPreview): string {
  if (!preview.providerId) return '';

  const playerUrl = `https://www.tiktok.com/player/v1/${preview.providerId}?controls=1&progress_bar=1&play_button=1&volume_control=1&fullscreen_button=1&timestamp=1&loop=0&autoplay=0&music_info=0&description=0&native_context_menu=0&rel=0`;
  const safeUrl = escapeHtmlAttribute(playerUrl);
  const safeTitle = escapeHtmlAttribute(preview.title || 'TikTok video');

  return `<!doctype html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <style>
    html, body { margin: 0; padding: 0; background: transparent; overflow: hidden; }
    .wrap { width: 100%; height: 100%; background: #000; }
    iframe { display: block; width: 100%; height: 100%; border: 0; background: #000; }
  </style>
</head>
<body>
  <div class="wrap">
    <iframe
      src="${safeUrl}"
      title="${safeTitle}"
      allow="fullscreen; encrypted-media; picture-in-picture"
      allowfullscreen>
    </iframe>
  </div>
</body>
</html>`;
}
